/**
 * OnboardingReadinessRing - Circular progress indicator per 5W1H dimension (DRR-184)
 *
 * Displays completion status for a single onboarding dimension using a ring progress
 * indicator. Click to expand shows incomplete tasks for that dimension.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Color coding: green (100%), yellow (partial), gray (0%)
 * - No domain-specific labels (uses generic 5W1H dimension names)
 */

import { useState } from 'react';
import {
  Badge,
  Collapse,
  Group,
  Paper,
  RingProgress,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCalendar,
  IconChevronDown,
  IconChevronUp,
  IconClipboardCheck,
  IconMapPin,
  IconRoute,
  IconShieldCheck,
  IconUser,
} from '@tabler/icons-react';
import type { DimensionStats, ReadinessLevel } from '@/hooks/useOnboardingReadiness';
import { READINESS_COLORS, READINESS_LABELS } from '@/hooks/useOnboardingReadiness';

// Map dimension IDs to icons (domain-agnostic)
const DIMENSION_ICONS: Record<string, typeof IconUser> = {
  WHO: IconUser,
  WHAT: IconClipboardCheck,
  WHY: IconShieldCheck,
  HOW: IconRoute,
  WHERE: IconMapPin,
  WHEN: IconCalendar,
};

export interface OnboardingReadinessRingProps {
  /** Dimension statistics from useOnboardingReadiness */
  dimension: DimensionStats;
  /** Whether to show the compact version (ring only, no text) */
  compact?: boolean;
  /** Size of the ring progress indicator */
  size?: number;
  /** Thickness of the ring */
  thickness?: number;
  /** Whether clicking expands to show incomplete task count */
  expandable?: boolean;
}

/**
 * Circular progress indicator for a single onboarding dimension
 *
 * @example
 * const { dimensions } = useOnboardingReadiness();
 * <OnboardingReadinessRing dimension={dimensions[0]} />
 */
export function OnboardingReadinessRing({
  dimension,
  compact = false,
  size = 60,
  thickness = 6,
  expandable = true,
}: OnboardingReadinessRingProps) {
  const [expanded, setExpanded] = useState(false);

  const Icon = DIMENSION_ICONS[dimension.id] ?? IconClipboardCheck;
  const color = READINESS_COLORS[dimension.readiness];
  const incomplete = dimension.total - dimension.completed;

  const ringContent = (
    <RingProgress
      size={size}
      thickness={thickness}
      roundCaps
      sections={[{ value: dimension.percentComplete, color }]}
      label={
        <Tooltip label={`${dimension.percentComplete}% complete`}>
          <Text ta="center" fw={600} size={size < 50 ? 'xs' : 'sm'}>
            {dimension.percentComplete}%
          </Text>
        </Tooltip>
      }
    />
  );

  if (compact) {
    return (
      <Tooltip
        label={
          <Stack gap={2}>
            <Text size="xs" fw={600}>{dimension.id}: {dimension.label}</Text>
            <Text size="xs">{dimension.completed}/{dimension.total} tasks</Text>
            <Badge size="xs" variant="light" color={color}>
              {READINESS_LABELS[dimension.readiness]}
            </Badge>
          </Stack>
        }
        multiline
        w={180}
      >
        {ringContent}
      </Tooltip>
    );
  }

  const content = (
    <Paper p="sm" withBorder>
      <Group gap="sm" wrap="nowrap" justify="space-between">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" color={dimension.color} size="md">
            <Icon size={16} />
          </ThemeIcon>
          <div>
            <Group gap={4}>
              <Text size="sm" fw={600}>{dimension.id}</Text>
              <Text size="sm" c="dimmed">: {dimension.label}</Text>
            </Group>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {dimension.description}
            </Text>
          </div>
        </Group>
        <Group gap="sm" wrap="nowrap">
          {ringContent}
          {expandable && (
            <ThemeIcon
              variant="subtle"
              color="gray"
              size="sm"
              style={{ cursor: 'pointer' }}
            >
              {expanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            </ThemeIcon>
          )}
        </Group>
      </Group>

      {expandable && (
        <Collapse in={expanded}>
          <Stack gap="xs" mt="sm" pl="md">
            <Group justify="space-between">
              <Text size="xs" c="dimmed">Completed</Text>
              <Badge size="xs" variant="light" color="green">
                {dimension.completed} tasks
              </Badge>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">Remaining</Text>
              <Badge size="xs" variant="light" color={incomplete > 0 ? 'orange' : 'green'}>
                {incomplete} tasks
              </Badge>
            </Group>
            {dimension.blocked > 0 && (
              <Group justify="space-between">
                <Text size="xs" c="dimmed">Blocked</Text>
                <Badge size="xs" variant="filled" color="red">
                  {dimension.blocked} tasks
                </Badge>
              </Group>
            )}
            <Group justify="space-between">
              <Text size="xs" c="dimmed">Status</Text>
              <Badge size="xs" variant="filled" color={color}>
                {READINESS_LABELS[dimension.readiness]}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed" ff="monospace">
              Task Groups: {dimension.taskGroupRefs.join(', ')}
            </Text>
          </Stack>
        </Collapse>
      )}
    </Paper>
  );

  if (expandable) {
    return (
      <UnstyledButton onClick={() => setExpanded((e) => !e)} style={{ width: '100%' }}>
        {content}
      </UnstyledButton>
    );
  }

  return content;
}

export default OnboardingReadinessRing;
